import { useState, useEffect, useContext } from "react";
import {
  Table,
  Form,
  Input,
  Button,
  Modal,
  Space,
  message,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { AuthContext } from "../../contexts/AuthContext";
import UploadMakes from "./components/UploadMakes";

const REACT_APP_BASE_API_URL = process.env.REACT_APP_BASE_API_URL;

const Makes = () => {
  const [makes, setMakes] = useState<any[]>([]);
  const [selectedMake, setSelectedMake] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"make" | "model">("make");
  const [form] = Form.useForm();
  const access = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchMakes();
  }, []);

  const fetchMakes = async () => {
    try {
      const response = await fetch(`${REACT_APP_BASE_API_URL}/v1/makes`);
      const data = await response.json();
      setMakes(data);
    } catch (error) {
      message.error("Failed to fetch makes.");
    }
  };

  const showModal = (
    type: "make" | "model",
    makeId: string | null = null,
    modelId: string | null = null,
    record: any = null
  ) => {

    setModalType(type);
    setSelectedMake(makeId);
    setSelectedModel(modelId);
    setSelectedRecord(record);

    if (record) {
      form.setFieldsValue(record);
    } else {
      form.resetFields();
    }

    setIsModalVisible(true);
  };

  const handleAddOrEdit = async (values: any) => {
    try {
      let url = "";
      let method = "POST";

      if (modalType === "make") {
        url = "/v1/makes";
        if (selectedRecord?._id) {
          url += `/${selectedRecord?._id}`;
          method = "PUT";
        }
      } else if (modalType === "model") {
        if (!selectedMake) {
          message.error("No make selected");
          return;
        }
        url = `/v1/makes/${selectedMake}/models`;
        if (selectedRecord?._id) {
          url += `/${selectedRecord?._id}`;
          method = "PUT";
        }
      }

      const response = await fetch(`${REACT_APP_BASE_API_URL}${url}`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        fetchMakes();
        message.success("Operation successful.");
        setIsModalVisible(false);
      } else {
        message.error("Operation failed.");
      }
    } catch (error) {
      message.error("Error occurred.");
    }
  };

  const handleDelete = async (
    type: string,
    id: string,
    makeId?: string,
    modelId?: string
  ) => {
    try {
      let url = "";
      if (type === "make") {
        url = `/v1/makes/${id}`;
      } else if (type === "model") {
        if (!makeId) {
          message.error("No make selected");
          return;
        }
        url = `/v1/makes/${makeId}/models/${id}`;
      }

      const response = await fetch(`${REACT_APP_BASE_API_URL}${url}`, {
        method: "DELETE",
      });
      if (response.ok) {
        fetchMakes();
        message.success("Deleted successfully.");
      } else {
        message.error("Failed to delete.");
      }
    } catch (error) {
      message.error("Error occurred while deleting.");
    }
  };

  const makeColumns: any = [
    {
      title: "Make Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showModal("make", null, null, record)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete("make", record?._id)}
          >
            Delete
          </Button>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => showModal("model", record?._id)}
          >
            Add Model
          </Button>
        </Space>
      ),
    },
  ];

  const modelColumns: any = [
    {
      title: "Model Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Model Year",
      dataIndex: "year",
      key: "year",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showModal("model", record.makeId, null, record)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete("model", record?._id, record.makeId)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];


  return (
    <div className="bg-gray-100">
      <div className="bg-white m-1">
        <div className="flex flex-col sm:flex-row justify-between items-center border px-2">
          <div className="">
            <div className="font-bold capitalize text-lg">
              All Makes and Models
            </div>
            <div className="text-gray-600 mb-1">Manage all makes and models</div>
          </div>

          <div className="flex">

            {access?.includes(user?.accessLevel) && (
              <div className="flex items-center">
                <button
                  onClick={() => setUploadModal(true)}
                  className="flex mx-2 justify-center items-center border px-4 h-8  bg-white  hover:bg-gray-200 text-autoinc shadow-sm"
                >
                  + Upload
                </button>

                <button
                  onClick={() => showModal("make")}
                  className="flex mx-2 justify-center items-center border px-4 h-8  bg-karimjee  hover:bg-karimjee-dark text-white shadow-sm"
                >
                  + New Make
                </button>

              </div>
            )}
          </div>
        </div>

        <div className="border p-2 shadow-sm">
          <div className="">
            <Table
              dataSource={makes}
              columns={makeColumns}
              rowKey={(record) => record?._id || Math.random()}
              expandable={{
                expandedRowRender: (make: any) => (
                  <Table
                    dataSource={
                      (make?.models ?? []).map((model: any) => ({
                        ...model,
                        makeId: make?._id,
                      })) || []
                    }
                    columns={modelColumns}
                    rowKey={(record) => record?._id || Math.random()}
                    pagination={false}
                  />
                ),
              }}
            />

            <Modal
              title={
                modalType === "make"
                  ? selectedRecord ? "Edit  Make" : "Add  Make"
                  : modalType === "model"
                    ? selectedRecord ? "Edit  Model" : "Add Model"
                    : null
              }
              open={isModalVisible}
              onCancel={() => setIsModalVisible(false)}
              onOk={() => {
                form
                  .validateFields()
                  .then((values) => {
                    handleAddOrEdit(values);
                  })
                  .catch(() => { });
              }}
            >
              <Form form={form} layout="vertical" initialValues={selectedRecord || {}}>
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: "Please enter the name." }]}
                >
                  <Input />
                </Form.Item>
              </Form>
            </Modal>
          </div>

        </div>
      </div>


      <UploadMakes
        isVisible={uploadModal}
        setVisible={setUploadModal}
        refetch={fetchMakes}
      />
    </div>
  );
};

export default Makes;
